import { Trans } from '@lingui/react/macro'
import { t } from '@lingui/core/macro'
import { getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { button } from '@tecsinapse/cortex-core'
import { Table as TableCortex } from '@tecsinapse/cortex-react'
import React, { useCallback } from 'react'
import { MdFileDownload } from 'react-icons/md'
import { Roles } from '@/core/enums/roles'
import { AccessControl } from '@/core/utils/access-control'
import { EmptyList } from '@/core/components/empty-list'
import { snackbar } from '@/core/utils/snackbar'
import { Table } from '@/core/components/index'
import { IMPORTAR_USUARIOS_QUERY } from '../data/importar-usuarios-query'
import { columnsTableImportStatus } from '../components/columns'
import { useRealm } from '@/core/hooks/use-realm'
import { usePortalAxios } from '@tecsinapse-org/portal-menu-kit'
import { useQuery } from '@tanstack/react-query'
import {
  ImportarUsuariosQueryQuery,
  ImportarUsuariosQueryQueryVariables
} from '../../../core/types/graphql'
import { request } from '../../../core/utils/request'
import { EventGoal, EventOrigin, logEvent } from '@/core/analytics'
import { AnalyticsEvents } from '@/core/analytics/analytics-eventes-enum'

export const ImportarUsuarios = () => {
  const {
    data,
    isLoading: loading,
    refetch
  } = useQuery({
    queryKey: ['importarUsuarios'],
    queryFn: async () =>
      await request<
        ImportarUsuariosQueryQuery,
        ImportarUsuariosQueryQueryVariables
      >(IMPORTAR_USUARIOS_QUERY),
    refetchInterval: 30000
  })

  const { instance } = usePortalAxios()

  const realm = useRealm()

  const table = useReactTable({
    data: data?.importStatus,
    getCoreRowModel: getCoreRowModel(),
    columns: columnsTableImportStatus()
  })

  const handleFileSelected = async (file: File) => {
    const data = new FormData()
    data.set('file', file)

    try {
      await instance.post('/import/users', data, {})
      snackbar.show(
        'success',
        t`Upload feito com sucesso! Você será notificado via email ao finalizar o processo de importação`
      )
      logEvent({
        name: AnalyticsEvents.IMPORTAR_USUARIOS_SUCESSO,
        params: {
          origin: EventOrigin.button,
          goal: EventGoal.success
        }
      })
    } catch (error) {
      console.error(error, ' error')
      snackbar.show(
        'error',
        t`Error ao fazer upload, verifique a extensão do arquivo, apenas .XLSX é permitido.`
      )
      logEvent({
        name: AnalyticsEvents.IMPORTAR_USUARIOS_ERRO,
        params: {
          origin: EventOrigin.button,
          goal: EventGoal.error
        }
      })
    }
  }

  const getModeloByRealm = useCallback(() => {
    switch (realm) {
      case 'PortalNissan':
        return '/template-importacao-usuarios+cargo+lms.xlsx'
      default:
        return '/template-importacao-usuarios+cargo.xlsx'
    }
  }, [realm])

  return (
    <div className={'mb-deca overflow-x-hidden'}>
      <div className={'flex justify-between mb-deca'}>
        <p className={'text-h1 font-bold'}>
          <Trans>Importações</Trans>
        </p>
        <AccessControl allowedRoles={[Roles.PORTAL_MANUTENCAO_USUARIOS]}>
          <div className={'flex flex-row space-x-deca'}>
            <a
              href={getModeloByRealm()}
              download='ModeloImportacaoUsuarios.xlsx'
              className={button({
                className: 'no-underline'
              })}
            >
              <MdFileDownload className={'mr-mili'} />
              <p>
                <Trans>Baixar modelo</Trans>
              </p>
            </a>
            <label htmlFor={'button-file'}>
              <input
                accept='application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                id='button-file'
                name='button-file'
                className={'hidden'}
                type={'file'}
                onChange={event => {
                  logEvent({
                    name: AnalyticsEvents.IMPORTAR_USUARIOS_INICIO,
                    params: {
                      origin: EventOrigin.button,
                      goal: EventGoal.upload
                    }
                  })
                  handleFileSelected(
                    event?.currentTarget?.files?.[0] as File
                  ).then(() => refetch())
                }}
              />
              <span className={button({ className: 'w-fit' })}>
                <Trans>Escolher arquivo</Trans>
              </span>
            </label>
          </div>
        </AccessControl>
      </div>
      <TableCortex className={'w-full'}>
        {data?.importStatus?.length > 0 || loading ? (
          <>
            <Table.HeaderColumns table={table} />
            <Table.Body table={table} loading={loading} />
          </>
        ) : (
          <EmptyList />
        )}
      </TableCortex>
    </div>
  )
}
